import React, { useMemo, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { Upload, Trash2, Loader2, Shield, Download, AlertTriangle, Mail, Share, Smartphone, Monitor } from "lucide-react";
import { signOut } from "firebase/auth";
import { useAppState } from "../hooks/useAppState";
import { useAuth } from "../hooks/useAuth";
import { uploadUserLogo, deleteUserLogo } from "../firebase/storageService";
import { exportUserData, deleteUserAccount } from "../firebase/firestoreService";
import { auth } from "../firebase/config";
import TopBar from "../components/layout/TopBar";
import SectionCard from "../components/sections/SectionCard";
import ChangeEmailModal from "../components/common/ChangeEmailModal";

function buildInvoiceCode(format, seq, year) {
  const safeFormat = (format || "{NN}-{YYYY}").trim();

  // Supporta token:
  // {N}   -> 1
  // {NN}  -> 01
  // {NNN} -> 001
  // {YYYY}-> 2026
  // {YY}  -> 26
  // {SEQ}, {SEQ2}, {SEQ3} -> per retrocompatibilità
  const yy = String(year).slice(-2);

  return safeFormat
    .replaceAll("{YEAR}", String(year))
    .replaceAll("{YYYY}", String(year))
    .replaceAll("{YY}", yy)
    .replaceAll("{SEQ3}", String(seq).padStart(3, "0"))
    .replaceAll("{SEQ2}", String(seq).padStart(2, "0"))
    .replaceAll("{SEQ}", String(seq))
    .replaceAll("{NNN}", String(seq).padStart(3, "0"))
    .replaceAll("{NN}", String(seq).padStart(2, "0"))
    .replaceAll("{N}", String(seq));
}

export default function ProfilePage() {
  const {
    user,
    setUser,
    darkMode,
    toggleDarkMode,
    selectedYear,
    nextSeq,
    isSynced,
  } = useAppState();

  const { user: authUser } = useAuth();

  // Form local (per edit senza salvare subito)
  const [firstName, setFirstName] = useState(user?.firstName || "");
  const [lastName, setLastName] = useState(user?.lastName || "");
  const [format, setFormat] = useState(user?.invoiceNumberTemplate || "{NN}-{YYYY}");

  // Logo upload state
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [pendingFile, setPendingFile] = useState(null);

  // GDPR state
  const [isExporting, setIsExporting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  // Change email state
  const [showChangeEmail, setShowChangeEmail] = useState(false);
  const isPasswordProvider = authUser?.providerData?.[0]?.providerId === 'password';

  const preview = useMemo(() => {
    const year = selectedYear || new Date().getFullYear();
    return buildInvoiceCode(format, nextSeq, year);
  }, [format, selectedYear, nextSeq]);

  const quickFormats = [
    { label: "01/2026", value: "{NN}/{YYYY}" },
    { label: "FAT-01-2026", value: "FAT-{NN}-{YYYY}" },
    { label: "CC-01-2026", value: "CC-{NN}-{YYYY}" },
    { label: "2026-001", value: "{YYYY}-{NNN}" },
  ];

  const onSave = () => {
    setUser({
      ...user,
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      invoiceNumberTemplate: format.trim() || "{NN}-{YYYY}",
    });

    alert("Profilo salvato");
  };

  // Logo handlers
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadError(null);

    // Validazione tipo
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setUploadError('Formato non supportato. Usa JPG, PNG o WebP.');
      return;
    }

    // Validazione dimensione (2MB)
    if (file.size > 2 * 1024 * 1024) {
      setUploadError('File troppo grande. Massimo 2MB.');
      return;
    }

    // Crea preview locale
    const objectUrl = URL.createObjectURL(file);
    setPreviewUrl(objectUrl);
    setPendingFile(file);
  };

  const handleUploadConfirm = async () => {
    if (!pendingFile || !authUser?.uid) return;

    setUploading(true);
    setUploadError(null);

    try {
      const url = await uploadUserLogo(authUser.uid, pendingFile);
      setUser({ ...user, logoUrl: url });
      setPreviewUrl(null);
      setPendingFile(null);
    } catch (err) {
      setUploadError(err.message || 'Errore durante upload.');
    } finally {
      setUploading(false);
    }
  };

  const handleUploadCancel = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(null);
    setPendingFile(null);
    setUploadError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveLogo = async () => {
    if (!authUser?.uid) return;
    if (!window.confirm('Rimuovere il logo?')) return;

    setUploading(true);
    setUploadError(null);

    try {
      await deleteUserLogo(authUser.uid);
      setUser({ ...user, logoUrl: '' });
    } catch (err) {
      setUploadError(err.message || 'Errore durante rimozione.');
    } finally {
      setUploading(false);
    }
  };

  // Get initials for avatar fallback
  const getInitials = () => {
    const f = firstName || user?.firstName || '';
    const l = lastName || user?.lastName || '';
    return (f.charAt(0) + l.charAt(0)).toUpperCase() || '?';
  };

  // GDPR handlers
  const handleExportData = async () => {
    if (!authUser?.uid) return;
    setIsExporting(true);
    try {
      const data = await exportUserData(authUser.uid);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `forfy-export-${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Errore export:', error);
      alert('Errore durante l\'export dei dati.');
    }
    setIsExporting(false);
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'ELIMINA' || !authUser?.uid) return;
    setIsDeleting(true);
    try {
      await deleteUserAccount(authUser.uid);
      await authUser.delete();
      await signOut(auth);
    } catch (error) {
      console.error('Errore eliminazione:', error);
      if (error.code === 'auth/requires-recent-login') {
        alert('Per sicurezza, devi rieffettuare il login prima di eliminare l\'account.');
      } else {
        alert('Errore durante l\'eliminazione dell\'account.');
      }
      setIsDeleting(false);
    }
  };

  return (
    <div className={`min-h-screen transition-colors ${darkMode ? 'bg-neutral-950 text-white' : 'bg-neutral-50 text-neutral-900'}`}>
      <TopBar
        user={user}
        notificationsCount={0}
        darkMode={darkMode}
        toggleDarkMode={toggleDarkMode}
        onUploadLogo={() => {}}
        isSynced={isSynced}
      />

      <main className="max-w-6xl mx-auto px-4 sm:px-6 pb-12">
        {/* Dati utente */}
        <div className="mt-6">
          <SectionCard
            title="Dati utente"
            subtitle="Queste informazioni vengono usate per personalizzare la dashboard."
            darkMode={darkMode}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={`text-xs ${darkMode ? 'text-neutral-500' : 'text-neutral-600'}`}>Nome</label>
                <input
                  className={`mt-1 w-full px-3 py-2 border rounded-lg text-sm ${
                    darkMode
                      ? 'border-neutral-700 bg-neutral-800 text-white'
                      : 'border-neutral-200 bg-white text-neutral-900'
                  }`}
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="Es. Matteo"
                />
              </div>

              <div>
                <label className={`text-xs ${darkMode ? 'text-neutral-500' : 'text-neutral-600'}`}>Cognome</label>
                <input
                  className={`mt-1 w-full px-3 py-2 border rounded-lg text-sm ${
                    darkMode
                      ? 'border-neutral-700 bg-neutral-800 text-white'
                      : 'border-neutral-200 bg-white text-neutral-900'
                  }`}
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Es. Rossi"
                />
              </div>
            </div>

            {/* Logo upload */}
            <div className="mt-6 pt-4 border-t border-neutral-200 dark:border-neutral-700">
              <label className={`text-xs ${darkMode ? 'text-neutral-500' : 'text-neutral-600'}`}>Logo</label>

              <div className="mt-2 flex items-center gap-4">
                {/* Avatar preview */}
                <div className={`w-16 h-16 rounded-full flex items-center justify-center overflow-hidden ${
                  darkMode ? 'bg-neutral-700' : 'bg-forfy-50'
                }`}>
                  {previewUrl ? (
                    <img src={previewUrl} alt="Preview" className="w-full h-full object-cover" />
                  ) : user?.logoUrl ? (
                    <img src={user.logoUrl} alt="Logo" className="w-full h-full object-cover" />
                  ) : (
                    <span className={`text-lg font-semibold ${darkMode ? 'text-neutral-400' : 'text-forfy-600'}`}>
                      {getInitials()}
                    </span>
                  )}
                </div>

                {/* Actions */}
                <div className="flex flex-col gap-2">
                  {pendingFile ? (
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={handleUploadConfirm}
                        disabled={uploading}
                        className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-forfy-500 text-white text-sm hover:bg-forfy-600 disabled:opacity-50"
                      >
                        {uploading ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Caricamento...
                          </>
                        ) : (
                          'Conferma'
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={handleUploadCancel}
                        disabled={uploading}
                        className={`px-3 py-1.5 rounded-lg border text-sm ${
                          darkMode
                            ? 'border-neutral-700 bg-neutral-800 text-neutral-300 hover:bg-neutral-700'
                            : 'border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50'
                        } disabled:opacity-50`}
                      >
                        Annulla
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                        className={`flex items-center gap-1 px-3 py-1.5 rounded-lg border text-sm ${
                          darkMode
                            ? 'border-neutral-700 bg-neutral-800 text-neutral-300 hover:bg-neutral-700'
                            : 'border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50'
                        } disabled:opacity-50`}
                      >
                        <Upload className="w-4 h-4" />
                        Carica logo
                      </button>

                      {user?.logoUrl && (
                        <button
                          type="button"
                          onClick={handleRemoveLogo}
                          disabled={uploading}
                          className={`flex items-center gap-1 px-3 py-1.5 rounded-lg border text-sm ${
                            darkMode
                              ? 'border-red-700 bg-red-900/30 text-red-400 hover:bg-red-900/50'
                              : 'border-red-200 bg-red-50 text-red-600 hover:bg-red-100'
                          } disabled:opacity-50`}
                        >
                          <Trash2 className="w-4 h-4" />
                          Rimuovi
                        </button>
                      )}
                    </div>
                  )}

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handleFileSelect}
                    className="hidden"
                  />

                  {uploadError && (
                    <div className="text-xs text-red-500">{uploadError}</div>
                  )}

                  <div className={`text-xs ${darkMode ? 'text-neutral-500' : 'text-neutral-500'}`}>
                    JPG, PNG o WebP. Massimo 2MB.
                  </div>
                </div>
              </div>
            </div>
          </SectionCard>
        </div>

        {/* Account */}
        <div className="mt-6">
          <SectionCard
            title="Account"
            subtitle="Gestisci le credenziali del tuo account."
            darkMode={darkMode}
          >
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div>
                <label className={`text-xs ${darkMode ? 'text-neutral-500' : 'text-neutral-600'}`}>Email di accesso</label>
                <div className={`mt-1 text-sm font-medium ${darkMode ? 'text-white' : 'text-neutral-900'}`}>
                  {authUser?.email || '—'}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowChangeEmail(true)}
                disabled={!isPasswordProvider}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm ${
                  darkMode
                    ? 'border-neutral-700 bg-neutral-800 text-neutral-300 hover:bg-neutral-700'
                    : 'border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                <Mail className="w-4 h-4" />
                Modifica email
              </button>
            </div>
            {!isPasswordProvider && (
              <p className={`mt-3 text-xs ${darkMode ? 'text-neutral-500' : 'text-neutral-500'}`}>
                Il cambio email è disponibile solo per account con email/password. Gli account Google devono gestire l'email da Google.
              </p>
            )}
          </SectionCard>
        </div>

        {/* Numero fattura */}
        <div className="mt-6">
          <SectionCard
            title="Formato numero fattura"
            subtitle="Scegli come costruire automaticamente il numero fattura (stringa)."
            darkMode={darkMode}
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div>
                <label className={`text-xs ${darkMode ? 'text-neutral-500' : 'text-neutral-600'}`}>
                  Formato (usa i placeholder)
                </label>
                <input
                  className={`mt-1 w-full px-3 py-2 border rounded-lg text-sm ${
                    darkMode
                      ? 'border-neutral-700 bg-neutral-800 text-white'
                      : 'border-neutral-200 bg-white text-neutral-900'
                  }`}
                  value={format}
                  onChange={(e) => setFormat(e.target.value)}
                  placeholder="Es. CC-{NN}-{YYYY}"
                />

                <div className="mt-2 flex flex-wrap gap-2">
                  {quickFormats.map((q) => (
                    <button
                      key={q.value}
                      type="button"
                      className={`px-3 py-1.5 rounded-lg border text-xs ${
                        darkMode
                          ? 'border-neutral-700 bg-neutral-800 text-neutral-400 hover:bg-neutral-700'
                          : 'border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50'
                      }`}
                      onClick={() => setFormat(q.value)}
                      title={q.value}
                    >
                      {q.label}
                    </button>
                  ))}
                </div>

                <div className={`mt-3 text-xs space-y-1 ${darkMode ? 'text-neutral-500' : 'text-neutral-500'}`}>
                  <div>
                    Placeholder supportati:{" "}
                    <b>{"{N}"}</b>, <b>{"{NN}"}</b>, <b>{"{NNN}"}</b>,{" "}
                    <b>{"{YYYY}"}</b>, <b>{"{YY}"}</b>
                  </div>
                  <div>
                    Esempio: <b>CC-{"{NN}"}-{"{YYYY}"}</b> → <b>CC-01-2026</b>
                  </div>
                </div>
              </div>

              <div className={`rounded-xl border p-4 ${
                darkMode
                  ? 'border-neutral-700 bg-neutral-800'
                  : 'border-neutral-200 bg-white'
              }`}>
                <div className={`text-xs ${darkMode ? 'text-neutral-500' : 'text-neutral-500'}`}>Anteprima</div>
                <div className={`mt-1 text-lg font-semibold ${darkMode ? 'text-white' : 'text-neutral-900'}`}>{preview}</div>
                <div className={`mt-2 text-xs ${darkMode ? 'text-neutral-500' : 'text-neutral-500'}`}>
                  Basata su anno selezionato in dashboard: <b>{selectedYear}</b>
                </div>

                <div className={`mt-4 rounded-lg border p-3 text-xs ${
                  darkMode
                    ? 'bg-neutral-700 border-neutral-600 text-neutral-400'
                    : 'bg-neutral-50 border-neutral-100 text-neutral-600'
                }`}>
                  Nota: il progressivo è gestito internamente. Anche se cambi formato, lo storico resta invariato.
                </div>
              </div>
            </div>
          </SectionCard>
        </div>

        {/* Installa Forfy - sezione informativa permanente, non dismissabile */}
        <div className="mt-6">
          <SectionCard
            title="Installa Forfy"
            subtitle="Aggiungi Forfy alla schermata Home del tuo dispositivo per un accesso più rapido, come un'app."
            darkMode={darkMode}
          >
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className={`rounded-xl border p-4 ${
                darkMode
                  ? 'border-neutral-700 bg-neutral-800'
                  : 'border-forfy-200 bg-forfy-50'
              }`}>
                <div className={`w-9 h-9 rounded-full flex items-center justify-center ${
                  darkMode ? 'bg-forfy-900/40' : 'bg-forfy-100'
                }`}>
                  <Share className={`w-4 h-4 ${darkMode ? 'text-forfy-300' : 'text-forfy-700'}`} />
                </div>
                <h3 className={`mt-3 text-sm font-semibold ${darkMode ? 'text-white' : 'text-neutral-900'}`}>
                  iPhone / iPad (Safari)
                </h3>
                <p className={`mt-1 text-xs ${darkMode ? 'text-neutral-400' : 'text-neutral-600'}`}>
                  Tocca l'icona Condividi in basso, poi "Aggiungi a Home".
                </p>
              </div>

              <div className={`rounded-xl border p-4 ${
                darkMode
                  ? 'border-neutral-700 bg-neutral-800'
                  : 'border-forfy-200 bg-forfy-50'
              }`}>
                <div className={`w-9 h-9 rounded-full flex items-center justify-center ${
                  darkMode ? 'bg-forfy-900/40' : 'bg-forfy-100'
                }`}>
                  <Smartphone className={`w-4 h-4 ${darkMode ? 'text-forfy-300' : 'text-forfy-700'}`} />
                </div>
                <h3 className={`mt-3 text-sm font-semibold ${darkMode ? 'text-white' : 'text-neutral-900'}`}>
                  Android (Chrome)
                </h3>
                <p className={`mt-1 text-xs ${darkMode ? 'text-neutral-400' : 'text-neutral-600'}`}>
                  Tocca ⋮ in alto a destra, poi "Aggiungi a schermata Home".
                </p>
              </div>

              <div className={`rounded-xl border p-4 ${
                darkMode
                  ? 'border-neutral-700 bg-neutral-800'
                  : 'border-forfy-200 bg-forfy-50'
              }`}>
                <div className={`w-9 h-9 rounded-full flex items-center justify-center ${
                  darkMode ? 'bg-forfy-900/40' : 'bg-forfy-100'
                }`}>
                  <Monitor className={`w-4 h-4 ${darkMode ? 'text-forfy-300' : 'text-forfy-700'}`} />
                </div>
                <h3 className={`mt-3 text-sm font-semibold ${darkMode ? 'text-white' : 'text-neutral-900'}`}>
                  Computer (Chrome / Edge)
                </h3>
                <p className={`mt-1 text-xs ${darkMode ? 'text-neutral-400' : 'text-neutral-600'}`}>
                  Clicca l'icona di installazione nella barra degli indirizzi.
                </p>
              </div>
            </div>
          </SectionCard>
        </div>

        {/* Preferenze cookie - sempre visibile (floating button Iubenda è disabilitato) */}
        <div className="mt-6">
          <SectionCard
            title="Privacy"
            darkMode={darkMode}
          >
            <button
              type="button"
              onClick={() => {
                if (window._iub?.cs?.api?.openPreferences) {
                  window._iub.cs.api.openPreferences();
                } else {
                  window.open('https://www.iubenda.com/privacy-policy/43603356', '_blank', 'noopener');
                }
              }}
              className={`flex items-center gap-2 w-full px-3 py-2.5 rounded-lg border text-sm text-left transition-colors ${
                darkMode
                  ? 'border-neutral-700 bg-neutral-800 text-neutral-300 hover:bg-neutral-700'
                  : 'border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50'
              }`}
              aria-label="Gestisci preferenze cookie"
            >
              <Shield className="w-4 h-4 shrink-0" />
              Gestisci preferenze cookie
            </button>
          </SectionCard>
        </div>

        {/* I tuoi dati (GDPR) */}
        <div className="mt-6">
          <SectionCard
            title="I tuoi dati"
            subtitle="Gestisci i tuoi dati personali in conformità al GDPR."
            darkMode={darkMode}
          >
            {/* Export dati */}
            <div className="mb-6">
              <button
                type="button"
                onClick={handleExportData}
                disabled={isExporting}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm transition-colors ${
                  darkMode
                    ? 'border-neutral-700 bg-neutral-800 text-neutral-300 hover:bg-neutral-700'
                    : 'border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50'
                } disabled:opacity-50`}
              >
                {isExporting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Download className="w-4 h-4" />
                )}
                {isExporting ? 'Esportazione in corso...' : 'Scarica i miei dati'}
              </button>
              <p className={`mt-1.5 text-xs ${darkMode ? 'text-neutral-500' : 'text-neutral-500'}`}>
                Scarica una copia di tutti i tuoi dati in formato JSON (Art. 20 GDPR)
              </p>
            </div>

            {/* Elimina account */}
            <div className={`pt-6 border-t ${darkMode ? 'border-neutral-700' : 'border-neutral-200'}`}>
              {!showDeleteConfirm ? (
                <div>
                  <button
                    type="button"
                    onClick={() => setShowDeleteConfirm(true)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-colors ${
                      darkMode
                        ? 'bg-red-900/20 text-red-400 hover:bg-red-900/30'
                        : 'bg-red-50 text-red-600 hover:bg-red-100'
                    }`}
                  >
                    <Trash2 className="w-4 h-4" />
                    Elimina il mio account
                  </button>
                  <p className={`mt-1.5 text-xs ${darkMode ? 'text-neutral-500' : 'text-neutral-500'}`}>
                    Elimina definitivamente il tuo account e tutti i dati associati (Art. 17 GDPR)
                  </p>
                </div>
              ) : (
                <div className={`p-4 rounded-xl border ${
                  darkMode
                    ? 'bg-red-900/20 border-red-800'
                    : 'bg-red-50 border-red-200'
                }`}>
                  <div className="flex items-start gap-3 mb-4">
                    <AlertTriangle className={`w-5 h-5 shrink-0 mt-0.5 ${darkMode ? 'text-red-400' : 'text-red-600'}`} />
                    <div>
                      <h3 className={`text-sm font-semibold ${darkMode ? 'text-red-200' : 'text-red-800'}`}>
                        Sei sicuro di voler eliminare il tuo account?
                      </h3>
                      <p className={`text-xs mt-1 ${darkMode ? 'text-red-300' : 'text-red-700'}`}>
                        Questa azione è <strong>irreversibile</strong>. Tutti i tuoi dati (fatture, clienti, timesheet, impostazioni fiscali) saranno eliminati definitivamente.
                      </p>
                    </div>
                  </div>

                  <p className={`text-xs mb-2 ${darkMode ? 'text-red-300' : 'text-red-700'}`}>
                    Scrivi <strong>ELIMINA</strong> per confermare:
                  </p>
                  <input
                    type="text"
                    value={deleteConfirmText}
                    onChange={(e) => setDeleteConfirmText(e.target.value)}
                    placeholder="ELIMINA"
                    className={`w-full px-3 py-2 border rounded-lg text-sm mb-4 ${
                      darkMode
                        ? 'border-red-700 bg-neutral-900 text-white placeholder:text-neutral-600'
                        : 'border-red-300 bg-white text-neutral-900 placeholder:text-neutral-400'
                    }`}
                  />

                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={handleDeleteAccount}
                      disabled={deleteConfirmText !== 'ELIMINA' || isDeleting}
                      className="px-4 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isDeleting ? 'Eliminazione in corso...' : 'Elimina definitivamente'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowDeleteConfirm(false);
                        setDeleteConfirmText('');
                      }}
                      className={`px-4 py-2 rounded-lg text-sm ${
                        darkMode
                          ? 'bg-neutral-700 text-neutral-300 hover:bg-neutral-600'
                          : 'bg-neutral-200 text-neutral-700 hover:bg-neutral-300'
                      }`}
                    >
                      Annulla
                    </button>
                  </div>
                </div>
              )}
            </div>
          </SectionCard>
        </div>

        {/* Actions */}
        <div className="mt-6 flex items-center justify-end gap-2">
          <Link
            to="/dashboard"
            className={`px-4 py-2 rounded-lg border text-sm ${
              darkMode
                ? 'border-neutral-700 bg-neutral-800 text-neutral-400 hover:bg-neutral-700'
                : 'border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50'
            }`}
          >
            Annulla
          </Link>
          <button
            className="px-4 py-2 rounded-lg bg-forfy-500 text-white text-sm hover:bg-forfy-600"
            onClick={onSave}
          >
            Salva
          </button>
        </div>

        <ChangeEmailModal
          isOpen={showChangeEmail}
          onClose={() => setShowChangeEmail(false)}
          darkMode={darkMode}
          currentEmail={authUser?.email}
        />
      </main>
    </div>
  );
}
